import "../../../Css/Common/Modals/CompleteInsightModal.css";
import React, { useState } from "react";
import { GlassModalHeader } from "./GlassModalHeader";
import { INSIGHT_CATEGORY, INSIGHT_STATUS } from "../../../Types/account";
import { updateInsightStatus } from "../../../Api/updateInsightStatus";
import { publishUserEvent } from "../../../Api/publishUserEvent";
import {
  InsightDismissedEvent,
  USER_EVENT_TYPE,
} from "../../../Types/userEvents";
import { getUserFromLS } from "../../../utils/getUserFromLS";
import { mapInsightCategoryToType } from "../../../utils/mapInsightCategoryToType";
import { resolveUserEventClientId } from "../../../utils/userEventClientId";

type DismissInsightModalProps = {
  insightId: number;
  insightType: INSIGHT_CATEGORY;
  accountId: string | null;
  clientId: string | null;
  industryId?: string | null;
  onClose: () => void;
  onDismiss: (insightId: number) => void;
};

export const DismissInsightModal: React.FC<DismissInsightModalProps> = ({
  insightId,
  insightType,
  accountId,
  clientId,
  industryId = null,
  onClose,
  onDismiss,
}) => {
  const user = getUserFromLS();
  const [comments, setComments] = useState("");

  const createEventId = () => {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const publishInsightDismissedEvent = () => {
    const event: InsightDismissedEvent = {
      eventId: createEventId(),
      eventType: USER_EVENT_TYPE.INSIGHT_DISMISSED,
      userId: user?.id ?? null,
      accountId,
      clientId: resolveUserEventClientId(clientId, user?.client_id) ?? "",
      contactId: null,
      industryId: insightType === INSIGHT_CATEGORY.INDUSTRY ? industryId : null,
      payload: {
        insightId,
        insightCategory: insightType,
      },
      metadata: {
        source_service: "intentico-react-app",
        version: null,
      },
      occurredAt: new Date(),
    };

    publishUserEvent(event, undefined, (error) => {
      console.warn("Failed to publish insight dismissed event", error);
    });
  };

  const handleDismiss = () => {
    const params = {
      insight_type: mapInsightCategoryToType(insightType),
      insight_id: insightId,
      new_status: INSIGHT_STATUS.DISMISSED,
      comments: comments.trim() || null,
      account_id: accountId,
    };

    updateInsightStatus(
      params,
      () => {
        publishInsightDismissedEvent();
        onDismiss(insightId);
      },
      () => {}
    );
  };

  return (
    <div className="completeInsightModal_container glassModal_shell glassModal_shell--danger">
      <GlassModalHeader
        variant="danger"
        kicker="Insight"
        title="Dismiss insight"
        subtitle={
          <>
            This action is{" "}
            <span className="completeInsightModal_subtitleEmphasis">
              permanent
            </span>{" "}
            and cannot be undone.
          </>
        }
        onClose={onClose}
        closeAriaLabel="Close dismiss insight"
      />
      <div className="completeInsightModal_contentContainer">
        <div
          className="completeInsightModal_infoCallout completeInsightModal_infoCallout--danger"
          role="alert"
        >
          <div className="completeInsightModal_infoIconWrap" aria-hidden>
            <span className="completeInsightModal_infoIcon fa-solid fa-triangle-exclamation" />
          </div>
          <p className="completeInsightModal_infoText">
            This insight will be removed from your homepage and all other pages.
          </p>
        </div>

        <div className="completeInsightModal_commentsBlock">
          <label
            className="completeInsightModal_fieldLabel"
            htmlFor="dismiss-insight-comments"
          >
            Additional comments
          </label>
          <textarea
            id="dismiss-insight-comments"
            className="completeInsightModal_commentsTextArea"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Tell us why this insight wasn’t useful…"
          />
          <p className="completeInsightModal_helperText">
            Your feedback helps improve future insights.
          </p>
        </div>

        <div className="completeInsightModal_footerButtonRow">
          <button
            type="button"
            className="completeInsightModal_cancelButton"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="completeInsightModal_primaryButton completeInsightModal_primaryButton--dismiss"
            onClick={handleDismiss}
          >
            <i className="fa-duotone fa-circle-xmark" aria-hidden />
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};
